import { usePreferencesStore } from "~/stores/usePreferencesStore";
import { useI18n } from "vue-i18n";
import { injectRequired } from "~/injectRequired";
import { SolrClientKey } from "~/injectionKeys";
import {
  convertCountstoMap,
  getCommentaryIndexByRanges,
} from "~/units/commentary/commentaryIndex";
import { getCommentaryTypeWithCountData } from "~/components/CommentaryType.util";
import type { TranslationAgnosticVidRange } from "~/units/scripture";

interface Config {
  range: TranslationAgnosticVidRange;
}

export const useCommentaryIndexAndTypeCountFromRange = (config: Config) => {
  const solrClient = injectRequired(SolrClientKey);

  const preferencesStore = usePreferencesStore();
  const i18n = useI18n();

  const locale = i18n.locale;

  const index = useComputedResultAsync(() =>
    getCommentaryIndexByRanges(
      [config.range],
      solrClient,
      preferencesStore.getLanguages(locale.value),
    ).map((data) => data.ranges[0]),
  );

  const indexComputed = computed(() => {
    const countsMap = convertCountstoMap(
      index.value?.commentaryTypesCounts ?? {},
    );
    const commentaryTypes = getCommentaryTypeWithCountData({
      locale: locale.value,
      counts: countsMap,
      otherLanguages: preferencesStore.getOtherLanguages(locale.value),
    });
    const countLocale = index.value?.languagesCounts[locale.value] ?? 0;
    const count = index.value?.count ?? 0;
    const range = index.value?.range ?? config.range;
    const maxCount = commentaryTypes.reduce((count, commentaryType) => {
      return count + (commentaryType.countData.maxCountOther ?? 0);
    }, 0);

    return { commentaryTypes, count, countLocale, maxCount, range };
  });

  return { index, indexComputed };
};
